import { Button, Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import { DestinationPlate } from "features/WorklistTools/shared/components/DestinationPlate";
import { NumberField } from "features/WorklistTools/shared/components/NumberField";
import { PlateBarcodeField } from "features/WorklistTools/shared/components/PlateBarcodeField";
import { PlateType } from "features/WorklistTools/shared/components/PlateType";
import {
  PlatePosition,
  SeedingStrategy,
  WorklistTools,
} from "features/WorklistTools/shared/interfaces";
import {
  getUniqueDestinationPlateBarcode,
  setDestPlateType,
} from "features/WorklistTools/shared/WorklistHelpers";
import { useGetPlateBarcodePreviewMutation } from "graphql/generated/schema-types";
import { useAppReduxDispatch, useAppReduxSelector } from "hooks/reduxHooks";
import { SelectableGroup } from "react-selectable-fast";
import { ConfirmDialog } from "shared-components/ModalsAndDialogs/ConfirmDialog";
import { useConfirm } from "state-provider/ConfirmDialogProvider/hooks";
import styled from "styled-components";
import {
  DestPlateInfoState,
  DestPlateStringKeys,
  ExternalThawToolActions,
  ExtThawDestPlateLabwareTypeInfo,
  IntermediatePlateInfoState,
} from "../state";
import { useExternalThawStateProvider } from "../state/StateProvider";
import { MinFinalWellVolume } from "./components/MinFinalWellVolume";
import { TopUpVol } from "./components/TopUpVol";
import { selectDestWells } from "./handlers/selectDestWells";

const StyledSelectableGroup = styled(SelectableGroup)`
  height: 320px;
  width: 100%; //425px;;
`;

const StyledPlateSettingsContainer = styled.div`
  margin-top: 10px;
  width: 100%;
`;

const StyledPlateActions = styled.div`
  width: 100%;
  margin-top: 7px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 10px;
`;

export const DestinationPlatesContainer = (destPlateTypeInfo: any) => {
  const { isConfirmed } = useConfirm();
  const { externalThawInternalState } = useExternalThawStateProvider();
  const dispatch = useAppReduxDispatch();
  const [getPlateBarcodePreview] = useGetPlateBarcodePreviewMutation();
  const sourcePlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present.sourcePlateInfo
  );
  const intPlateInfo: IntermediatePlateInfoState[] = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present.intPlateInfo
  );
  const destPlateInfo: DestPlateInfoState[] = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present.destPlateInfo
  );

  const worklistValues = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present.worklistValues
  );

  const handleDestinationWellSelection = (destSelection: any[]) => {
    selectDestWells(
      externalThawInternalState,
      intPlateInfo,
      destPlateInfo,
      worklistValues,
      dispatch,
      destSelection,
      isConfirmed
    );
  };

  const handlePlateTypeChange = (
    index: number,
    plateTypeInfo: ExtThawDestPlateLabwareTypeInfo
  ) => {
    plateTypeInfo = setDestPlateType(plateTypeInfo);
    const sourcePlateBarcodes = sourcePlateInfo
      .filter((sourcePlate) => sourcePlate.plateBarcode !== "")
      .map((sourcePlate) => sourcePlate.plateBarcode);

    getPlateBarcodePreview({
      variables: {
        methodId: 148,
        sourcePlateBarcodes: sourcePlateBarcodes,
        destPlateCode:
          plateTypeInfo.defaultArrayPlateCode ?? plateTypeInfo.labwarePlateType,
      },
    }).then((data) => {
      const plates = data!.data!.plateBarcodePreview!;

      const destPlateBarcode = getUniqueDestinationPlateBarcode(
        plates.find((plate) => plate?.plateType === "Dest")?.plateBarcode ?? "",
        destPlateInfo
      );

      dispatch(
        ExternalThawToolActions.SET_DESTINATION_PLATE({
          index,
          rows: plateTypeInfo.plateRows,
          cols: plateTypeInfo.plateCols,
          operatingVol: plateTypeInfo.defaultLabwareVolume,
          minOperatingVol: plateTypeInfo.minLabwareVolume,
          labwareTypeCode: plateTypeInfo.labwareTypeCode,
          plateBarcode: destPlateBarcode,
          topup: plateTypeInfo?.topup ?? true,
          topUpVol: plateTypeInfo.defaultLabwareVolume, //Initally set as plate's OperatingVol
          startsInIncubator: plateTypeInfo?.startsInIncubator ?? true,
          endsInIncubator: plateTypeInfo?.endsInIncubator ?? true,
          preprocess: plateTypeInfo?.preprocess ?? true,
        })
      );
    });
  };

  const handleDestPlateSettingsChanged = (
    index: number,
    key: DestPlateStringKeys,
    value: string | number | boolean
  ) => {
    dispatch(
      ExternalThawToolActions.UPDATE_DESTINATION_PLATE_INFO({
        index,
        key,
        value,
      })
    );
  };

  const handleAddDestinationPlateClick = () => {
    dispatch(ExternalThawToolActions.ADD_DESTINATION_PLATE({}));
  };

  const handleRemovePlateClick = (index: number) => {
    dispatch(
      ExternalThawToolActions.CLEAR_DESTINATION_PLATE({
        index,
      })
    );
  };

  const handleClonePlateClick = async (index: number) => {
    if (
      destPlateInfo.length > index + 1 &&
      destPlateInfo[index + 1].labwareTypeCode !== ""
    ) {
      const confirmed = await isConfirmed(
        `Are you sure you want to clone the plate? This will overwrite the plate info in destination position ${
          index + 2
        }`
      );

      if (!confirmed) {
        return;
      }
    }

    if (destPlateInfo.length <= index + 1) {
      dispatch(ExternalThawToolActions.ADD_DESTINATION_PLATE({}));
    }

    const DestPlateLabwareTypeInfo = {
      plateRows: destPlateInfo[index].rows,
      plateCols: destPlateInfo[index].cols,
      labwareTypeCode: destPlateInfo[index].labwareTypeCode,
      defaultLabwareVolume: destPlateInfo[index].operatingVol,
      topup: destPlateInfo[index].topup,
      preprocess: destPlateInfo[index].preprocess,
      startsInIncubator: destPlateInfo[index].startsInIncubator,
      endsInIncubator: destPlateInfo[index].endsInIncubator,
    };
    handlePlateTypeChange(index + 1, DestPlateLabwareTypeInfo);
    dispatch(
      ExternalThawToolActions.CLONE_DESTINATION_PLATE({
        destinationIndex: index,
      })
    );
  };

  const volumetricWithDestWells = () => {
    return (
      worklistValues.int1ToDest &&
      externalThawInternalState.methodSettings.seedingStrategy ===
        SeedingStrategy.Volumetric
    );
  };

  return (
    <div
      style={{
        height: "calc(100vh - 300px)",
        width: "600px",
        overflow: "auto",
      }}
    >
      {destPlateInfo.map((item: DestPlateInfoState, index: number) => (
        <div style={{ padding: "20px", width: "100%" }}>
          <PlateBarcodeField
            plateRole={PlatePosition.Destination}
            index={index}
            label={"Destination Plate Barcode"}
            plateBarcode={item.plateBarcode}
            handlePlateBarcodeChange={(index, field, value) =>
              handleDestPlateSettingsChanged(index!, field, value)
            }
          />
          <StyledSelectableGroup
            onSelectionFinish={handleDestinationWellSelection}
            resetOnStart={true}
          >
            <DestinationPlate
              worklistTool={WorklistTools.ExternalThaw}
              index={index}
              plateRows={item.rows}
              plateCols={item.cols}
              wellMapping={[...worklistValues.int1ToDest]}
              sourcePlatePosition={PlatePosition.Intermediate}
              sourcePlateInfo={sourcePlateInfo}
            />
          </StyledSelectableGroup>
          <StyledPlateActions>
            <Button
              variant="outlined"
              disabled={item.rows.length < 1}
              onClick={() => handleRemovePlateClick(index)}
            >
              Remove Plate
            </Button>
            <Button
              variant="outlined"
              disabled={item.rows.length < 1}
              onClick={() => handleClonePlateClick(index)}
            >
              Clone Plate
            </Button>
          </StyledPlateActions>
          <StyledPlateSettingsContainer>
            <PlateType
              platePosition={PlatePosition.Destination}
              isDisabled={
                (index > 0 && destPlateInfo[index - 1].rows.length === 0) ||
                externalThawInternalState.methodSettings!.selectedSystem === 0
              }
              acceptedPlateTypes={destPlateTypeInfo.destPlateInfo}
              plateType={item.labwareTypeCode}
              setPlateType={(plateTypeInfo) =>
                handlePlateTypeChange(index, plateTypeInfo)
              }
            />
            {volumetricWithDestWells() ? (
              <MinFinalWellVolume int1ToDest={worklistValues.int1ToDest} />
            ) : null}
            <FormGroup sx={{ marginTop: "15px", alignItems: "flex-start" }} row>
              <FormControlLabel
                control={
                  <Checkbox
                    name="Topup"
                    checked={destPlateInfo[index].topup}
                    onChange={(e) =>
                      handleDestPlateSettingsChanged(
                        index,
                        "topup",
                        e.target.checked
                      )
                    }
                  />
                }
                label="Topup"
              />
              {destPlateInfo[index].topup && (
                <>
                  <TopUpVol
                    destPlateInfo={destPlateInfo[index]}
                    pelletResuspensionVol={6}
                    operatingVol={item.operatingVol}
                    minOperatingVol={item.minOperatingVol}
                  />

                  <NumberField
                    style={{ width: "30%" }}
                    size="small"
                    id="operating-vol"
                    label="Operating Vol (µL)"
                    value={destPlateInfo[index].operatingVol}
                    onChange={(value) =>
                      handleDestPlateSettingsChanged(
                        index,
                        "operatingVol",
                        parseInt(value)
                      )
                    }
                    disabled={true}
                  />
                </>
              )}
            </FormGroup>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    name="Preprocess Plate"
                    checked={destPlateInfo[index].preprocess}
                    onChange={(e) => {
                      handleDestPlateSettingsChanged(
                        index,
                        "preprocess",
                        e.target.checked
                      );
                    }}
                  />
                }
                label="Preprocess Plate"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="StartsInIncubator"
                    checked={destPlateInfo[index].startsInIncubator}
                    onChange={(e) =>
                      handleDestPlateSettingsChanged(
                        index,
                        "startsInIncubator",
                        e.target.checked
                      )
                    }
                  />
                }
                label="Starts In Incubator"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="EndsInIncubator"
                    checked={destPlateInfo[index].endsInIncubator}
                    onChange={(e) =>
                      handleDestPlateSettingsChanged(
                        index,
                        "endsInIncubator",
                        e.target.checked
                      )
                    }
                  />
                }
                label="Ends In Incubator"
              />
            </FormGroup>
          </StyledPlateSettingsContainer>
        </div>
      ))}

      <div style={{ display: "flex", justifyContent: "center" }}>
        <Button
          variant="outlined"
          onClick={() => handleAddDestinationPlateClick()}
        >
          Add Plate
        </Button>
      </div>
      <ConfirmDialog />
    </div>
  );
};
